import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"
}
const _hoisted_2 = ["src", "autoplay", "muted", "loop", "playsinline"]
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = {
  key: 3,
  class: "absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center rounded"
}
const _hoisted_5 = {
  key: 4,
  class: "absolute bottom-1 right-1 bg-black/20 text-white/70 text-xs px-1 rounded"
}

import { ref, onMounted, watch, computed } from 'vue';
import { centralizedImageSecurity, transformCloudinaryUrl } from '../../services/centralizedImageSecurity';
import { isLikelyVideoUrl } from '../../utils/validation';

interface Props {
  imageUrl: string;
  wallpaperId: string;
  isPreview?: boolean;
  alt?: string;
  containerClass?: string;
  imageClass?: string;
  targetAspect?: number; // desired aspect ratio (w/h)
  targetWidth?: number;  // suggested pixel width for Cloudinary
  targetHeight?: number; // optional pixel height
  cropMode?: 'fill' | 'fit' | 'pad';
  // Live wallpaper props
  forceAutoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsinline?: boolean;
}

interface Emits {
  (e: 'click'): void;
  (e: 'load'): void;
  (e: 'error'): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SimpleSecureImage',
  props: {
    imageUrl: {},
    wallpaperId: {},
    isPreview: { type: Boolean, default: true },
    alt: { default: 'Wallpaper' },
    containerClass: { default: '' },
    imageClass: { default: '' },
    targetAspect: {},
    targetWidth: {},
    targetHeight: {},
    cropMode: { default: 'fill' },
    forceAutoplay: { type: Boolean, default: false },
    muted: { type: Boolean, default: true },
    loop: { type: Boolean, default: true },
    playsinline: { type: Boolean, default: true }
  },
  emits: ["click", "load", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Reactive state
const loading = ref(true);
const error = ref(false);
const secureImageUrl = ref<string>('');
const imageRef = ref<HTMLImageElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const isVideoUrl = ref(false);
const isPosterFrameUrl = (url: string): boolean => /f_jpg|f_png|f_webp/i.test(url) || /so_0,.*f_jpg/i.test(url);
const shouldRenderVideo = computed(() => isVideoUrl.value && !isPosterFrameUrl(props.imageUrl));

// Load secure image
const loadSecureImage = async () => {
  if (!props.imageUrl || !props.wallpaperId) {
    error.value = true;
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = false;
    
    // Check if this is a poster frame URL (generated from video but returns an image)
    // Poster frame URLs contain transformations like f_jpg or so_0,...
    const isPosterFrame = isPosterFrameUrl(props.imageUrl);
    
    // Only treat as video if it's a video URL AND not a poster frame
    isVideoUrl.value = isLikelyVideoUrl(props.imageUrl) && !isPosterFrame;

    // For poster frames or regular images: apply Cloudinary transformations
    // For raw videos: pass through without transformations (security service handles videos separately)
    const transformedUrl = (isVideoUrl.value || isPosterFrame)
      ? props.imageUrl
      : transformCloudinaryUrl(props.imageUrl, {
          width: props.targetWidth,
          height: props.targetHeight,
          aspectRatio: props.targetAspect,
          cropMode: props.cropMode
        });

    const result = await centralizedImageSecurity.getSecureImageUrl({
      imageUrl: transformedUrl,
      wallpaperId: props.wallpaperId,
      isPreview: props.isPreview
    });

    secureImageUrl.value = result.secureUrl;
    
    if (!result.isSecure && props.isPreview) {
      console.warn('[SimpleSecureImage] Using fallback URL for preview');
    }

  } catch (err) {
    console.error('[SimpleSecureImage] Failed to load secure image:', err);
    error.value = true;
  } finally {
    loading.value = false;
  }
};

// Media event handlers
const onMediaLoad = () => {
  loading.value = false;
  
  // Apply security protections
  if (imageRef.value) {
    centralizedImageSecurity.protectImageElement(imageRef.value);
  } else if (videoRef.value) {
    centralizedImageSecurity.protectVideoElement(videoRef.value);
  }
  
  emit('load');
};

const onMediaError = () => {
  loading.value = false;
  error.value = true;
  emit('error');
};

// Watch for prop changes
watch([() => props.imageUrl, () => props.wallpaperId], () => {
  loadSecureImage();
}, { immediate: false });

// Initialize
onMounted(() => {
  loadSecureImage();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([_ctx.containerClass, "relative"])
  }, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1))
      : _createCommentVNode("", true),
    (secureImageUrl.value && shouldRenderVideo.value)
      ? (_openBlock(), _createElementBlock("video", {
          key: 1,
          src: secureImageUrl.value,
          class: _normalizeClass([_ctx.imageClass, "w-full h-full"]),
          onLoadeddata: onMediaLoad,
          onError: onMediaError,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('click'))),
          ref_key: "videoRef",
          ref: videoRef,
          autoplay: _ctx.isPreview || _ctx.forceAutoplay,
          muted: _ctx.muted,
          loop: _ctx.loop,
          playsinline: _ctx.playsinline
        }, null, 42, _hoisted_2))
      : (secureImageUrl.value)
        ? (_openBlock(), _createElementBlock("img", {
            key: 2,
            src: secureImageUrl.value,
            alt: _ctx.alt,
            class: _normalizeClass([_ctx.imageClass, "w-full h-full"]),
            onLoad: onMediaLoad,
            onError: onMediaError,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('click'))),
            ref_key: "imageRef",
            ref: imageRef
          }, null, 42, _hoisted_3))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[2] || (_cache[2] = [
              _createElementVNode("span", { class: "text-gray-500 text-sm" }, "Failed to load", -1)
            ])))
          : _createCommentVNode("", true),
    (_ctx.isPreview && !loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Preview "))
      : _createCommentVNode("", true)
  ], 2))
}
}

})